// Knowledge Base API対応のチャットサービス（非同期処理特化版）
import { appConfig } from '../config/env';
import { 
  KnowledgeBaseResponse, 
  BedrockError, 
  Citation 
} from '../types/ApiTypes';

// セッション管理用のインターフェース
export interface ChatSession {
  sessionId?: string;
  messageHistory: Array<{
    query: string;
    response: string;
    timestamp: Date;
  }>;
}

// 非同期処理対応のKnowledge Base API
export const queryAsyncKnowledgeBase = async (
  query: string,
  idToken: string,
  userId?: string,
  maxResults?: number
): Promise<{ requestId: string; status: string; message: string }> => {
  const requestBody = {
    query,
    ...(userId && { user_id: userId }),
    ...(maxResults && { max_results: maxResults })
  };

  const apiUrl = `${appConfig.knowledgeBaseApiUrl}/api/knowledge-base-async`;

  if (appConfig.debugMode) {
    console.log(`[DEBUG] Calling Async Knowledge Base API (Cognito): ${apiUrl}`);
    console.log(`[DEBUG] Request:`, requestBody);
  }

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Async Knowledge Base API Error (${response.status}): ${errorText}`);
  }

  const data = await response.json();
  
  if (appConfig.debugMode) {
    console.log(`[DEBUG] Async Knowledge Base Response:`, data);
  }

  return data;
};

// 非同期処理ステータス確認
export const checkAsyncStatus = async (
  requestId: string,
  idToken: string
): Promise<any> => {
  const apiUrl = `${appConfig.knowledgeBaseApiUrl}/api/knowledge-base-status?requestId=${requestId}`;

  if (appConfig.debugMode) {
    console.log(`[DEBUG] Checking Async Status (Cognito): ${apiUrl}`);
  }

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Async Status API Error (${response.status}): ${errorText}`);
  }

  const data = await response.json();
  
  if (appConfig.debugMode) {
    console.log(`[DEBUG] Async Status Response:`, data);
  }

  return data;
};

// セッション管理ヘルパー関数
export const createChatSession = (): ChatSession => {
  return {
    sessionId: undefined,
    messageHistory: []
  };
};

export const addToSession = (
  session: ChatSession, 
  query: string, 
  response: KnowledgeBaseResponse
): ChatSession => {
  return {
    sessionId: response.session_id,
    messageHistory: [
      ...session.messageHistory,
      {
        query,
        response: response.generated_response,
        timestamp: new Date()
      }
    ]
  };
};

// 引用情報の整理ヘルパー関数
export const formatCitations = (citations: Citation[]): Array<{
  text: string;
  sources: Array<{
    content: string;
    metadata: Record<string, any>;
  }>;
}> => {
  return citations.map(citation => ({
    text: citation.generated_response_part.textResponsePart.text,
    sources: citation.retrieved_references
  }));
};
